import type { Activity } from '@/api/types';

// Ports der vanilla-Helfer aus CLIENT_COMMON (definiert in functions/trips/[[path]].js).
// Eine Activity kann sowohl 'act' (mit Künstler) als auch 'meetup' (Treffpunkt) sein;
// fehlende Felder werden defensiv mit Defaults aufgefüllt.

export function actTitle(a: Activity): string {
  if (a.type === 'meetup') {
    return a.title?.trim() || 'Treffpunkt';
  }
  return a.artist?.trim() || a.title?.trim() || 'Act';
}

export function actLocation(a: Activity): string {
  const parts = [a.area?.trim(), a.address?.trim() || a.location?.trim()].filter(Boolean);
  return parts.join(' · ');
}

/**
 * Sortkey für die chronologische Sortierung. Acts ohne Uhrzeit landen am
 * Ende des Tages (24:00 als Sentinel), damit sie sichtbar bleiben.
 */
export function sortKey(a: Activity): string {
  const date = a.date || '9999-12-31';
  const time = a.time || '24:00';
  return `${date} ${time}`;
}

/** Millisekunden seit Epoch für den Act-Start. Liefert NaN bei fehlendem Datum. */
export function actStartMs(a: Activity): number {
  if (!a.date) return Number.NaN;
  const time = a.time || '00:00';
  // Madrid-Zeitzone — fest verdrahtet, passend zum Backend (madridTodayDate).
  // Vereinfacht: erzeugen wir eine ISO-Stamp mit Europe/Madrid Offset, das
  // exakt ist nur, wenn Madrid +02:00 (Sommerzeit Mai–Okt) — ausreichend
  // für die Trip-Periode. Bei Bedarf später durch Intl.DateTimeFormat ersetzen.
  return Date.parse(`${a.date}T${time}:00+02:00`);
}

/** Schätzung des Act-Endes. Ohne explizite Dauer: 90 Min nach Start. */
export function actEndMs(a: Activity): number {
  const start = actStartMs(a);
  if (Number.isNaN(start)) return Number.NaN;
  return start + 90 * 60 * 1000;
}

export function isNow(a: Activity, now: number = Date.now()): boolean {
  const start = actStartMs(a);
  const end = actEndMs(a);
  if (Number.isNaN(start)) return false;
  return now >= start && now <= end;
}

export function isPast(a: Activity, now: number = Date.now()): boolean {
  const end = actEndMs(a);
  if (Number.isNaN(end)) return false;
  return now > end;
}

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

/** "Montag, 25.05.2026" — wenn das Datum gültig ist, sonst Roh-String. */
export function dayLabelLong(date: string): string {
  if (!date) return '';
  const d = new Date(`${date}T12:00:00+02:00`);
  if (Number.isNaN(d.getTime())) return date;
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${WEEKDAYS[d.getUTCDay()]}, ${day}.${month}.${d.getUTCFullYear()}`;
}

/** "Uhrzeit folgt" wenn ohne Zeit, sonst "20:00". */
export function timeOrFolgt(a: Activity): string {
  return a.time?.trim() || 'Uhrzeit folgt';
}

/** Pill-Text für den Entry-Type ("Frei", "Eintritt: 15€", …). */
export function entryLabel(a: Activity): string | null {
  const entry = a.entry?.trim();
  if (!entry || entry.toLowerCase() === 'unknown') return null;
  if (entry.toLowerCase() === 'frei') return 'Frei';
  const price = a.price?.trim();
  if (price) return `${entry}: ${price}`;
  return entry;
}

/** Gruppiert eine Liste von Activities nach `date` und sortiert beides chronologisch. */
export interface ActivityDayGroup {
  date: string;
  acts: Activity[];
}

export function groupActsByDay(acts: Activity[]): ActivityDayGroup[] {
  const map = new Map<string, Activity[]>();
  for (const a of acts) {
    const date = a.date || '';
    const existing = map.get(date);
    if (existing) {
      existing.push(a);
    } else {
      map.set(date, [a]);
    }
  }
  const groups: ActivityDayGroup[] = Array.from(map.entries()).map(([date, list]) => ({
    date,
    acts: [...list].sort((x, y) => sortKey(x).localeCompare(sortKey(y))),
  }));
  // Tage ohne Datum landen ans Ende ('').
  groups.sort((x, y) => {
    if (!x.date) return 1;
    if (!y.date) return -1;
    return x.date.localeCompare(y.date);
  });
  return groups;
}
